/**
 * Sidebar Provider
 * Shared sidebar state management for all portals
 * Handles responsive behavior and localStorage persistence
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'

// ============================================================================
// Types
// ============================================================================

export type SidebarState = 'expanded' | 'collapsed' | 'hidden'

export interface SidebarContextValue {
  /** Current sidebar state */
  state: SidebarState
  /** Whether mobile drawer is open */
  mobileOpen: boolean
  /** Whether viewport is mobile (<768px) */
  isMobile: boolean
  /** Whether viewport is tablet (768-1024px) */
  isTablet: boolean
  /** Whether viewport is a large portrait kiosk screen (e.g. 32-inch portrait ~1080×1920) */
  isKioskPortrait: boolean
  /** Toggle sidebar collapsed/expanded on desktop */
  toggle: () => void
  /** Open mobile drawer */
  openMobile: () => void
  /** Close mobile drawer */
  closeMobile: () => void
  /** Collapse sidebar */
  collapse: () => void
  /** Expand sidebar */
  expand: () => void
}

// ============================================================================
// Constants
// ============================================================================

const SIDEBAR_STATE_KEY = 'spcc-sidebar-state'
const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024

// ============================================================================
// Context
// ============================================================================

const SidebarContext = createContext<SidebarContextValue | null>(null)

// ============================================================================
// Provider Component
// ============================================================================

export interface SidebarProviderProps {
  children: ReactNode
  /** Default state for desktop */
  defaultState?: SidebarState
  /** Custom storage key */
  storageKey?: string
}

export function SidebarProvider({
  children,
  defaultState = 'expanded',
  storageKey = SIDEBAR_STATE_KEY,
}: SidebarProviderProps) {
  const [state, setState] = useState<SidebarState>(() => {
    if (typeof window === 'undefined') return defaultState
    const saved = localStorage.getItem(storageKey)
    return (saved as SidebarState) || defaultState
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [isKioskPortrait, setIsKioskPortrait] = useState(false)

  // Handle viewport changes — debounced to prevent excessive re-renders during resize
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      const newIsMobile = width < MOBILE_BREAKPOINT
      const newIsTablet = width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT
      // Large portrait screen: 32-inch kiosk rotated to portrait (~1080×1920)
      // Threshold: ≥1000px wide, ≥1400px tall, and taller than wide
      const newIsKioskPortrait = width >= 1000 && height >= 1400 && height > width

      setIsMobile(newIsMobile)
      setIsTablet(newIsTablet)
      setIsKioskPortrait(newIsKioskPortrait)

      // Auto-collapse on tablet and kiosk portrait; hide on mobile
      if (newIsMobile) {
        setState('hidden')
        setMobileOpen(false)
      } else if (newIsTablet || newIsKioskPortrait) {
        setState('collapsed')
      } else {
        // Restore saved state on desktop
        const saved = localStorage.getItem(storageKey)
        if (saved && (saved === 'expanded' || saved === 'collapsed')) {
          setState(saved)
        }
      }
    }

    let resizeTimer: ReturnType<typeof setTimeout>
    const handleResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(checkViewport, 100)
    }

    checkViewport()
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(resizeTimer)
      window.removeEventListener('resize', handleResize)
    }
  }, [storageKey])

  // Persist state changes (not for kiosk portrait — keep it always collapsed)
  useEffect(() => {
    if (!isMobile && !isTablet && !isKioskPortrait && state !== 'hidden') {
      localStorage.setItem(storageKey, state)
    }
  }, [state, isMobile, isTablet, isKioskPortrait, storageKey])

  // Close mobile drawer on route change — use a ref to avoid re-registering on every mobileOpen change
  const mobileOpenRef = useRef(mobileOpen)
  useEffect(() => {
    mobileOpenRef.current = mobileOpen
  }, [mobileOpen])

  useEffect(() => {
    const handleRouteChange = () => {
      if (mobileOpenRef.current) setMobileOpen(false)
    }
    window.addEventListener('popstate', handleRouteChange)
    return () => window.removeEventListener('popstate', handleRouteChange)
  }, [])

  // Actions
  const toggle = useCallback(() => {
    setState((prev) => (prev === 'expanded' ? 'collapsed' : 'expanded'))
  }, [])

  const collapse = useCallback(() => setState('collapsed'), [])
  const expand = useCallback(() => setState('expanded'), [])
  const openMobile = useCallback(() => setMobileOpen(true), [])
  const closeMobile = useCallback(() => setMobileOpen(false), [])

  // Memoize context value so consumers only re-render when values actually change
  const contextValue = useMemo(
    () => ({
      state,
      mobileOpen,
      isMobile,
      isTablet,
      isKioskPortrait,
      toggle,
      openMobile,
      closeMobile,
      collapse,
      expand,
    }),
    [state, mobileOpen, isMobile, isTablet, isKioskPortrait, toggle, openMobile, closeMobile, collapse, expand]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
